// Cross-platform realtime-connection state, surfaced to the user by
// `ConnectivityBanner`. Both apps feed it from their own socket layer:
//   - web    → `apps/web/src/core/network/socket.ts` (leader-aware; followers
//              mirror the leader's state over the existing BroadcastChannel)
//   - desktop → `apps/desktop/src/network/socketAdapter.ts`
// In-memory only — connection status is ephemeral and must never persist.

import { create } from 'zustand';

/** The four phases a realtime socket moves through. Mirrors the web socket's
 *  own `ConnState` so the platform layers can forward it verbatim. */
export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

interface ConnectionStore {
  /** Current realtime-connection phase. Starts optimistic (`connected`) so a
   *  healthy reload never flashes a banner before the socket has reported. */
  readonly state: ConnectionState;
  /** Epoch ms the current `state` began — drives the banner's timing copy. */
  readonly since: number;
  setState(state: ConnectionState, nowMs: number): void;
}

export const useConnectionStore = create<ConnectionStore>((set, get) => ({
  state: 'connected',
  since: 0,
  setState: (state, nowMs) => {
    // No-op identical transitions so the banner's effects don't re-fire (and
    // the "Back online" confirmation doesn't retrigger) on duplicate reports.
    if (get().state === state) return;
    set({ state, since: nowMs });
  },
}));

/** Imperative setter for the socket layers (called outside React). */
export function setConnectionState(state: ConnectionState): void {
  useConnectionStore.getState().setState(state, Date.now());
}
